import React, { useEffect, useMemo, useState } from 'react';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../shared/components/modals/ConfirmationModal';
import OverrideBadge, { OverrideBadgeLegend } from '../../shared/components/common/OverrideBadge';
import DeveloperSettingsCard from './DeveloperSettingsCard';
import NotificationMatrixHelpModal from './NotificationMatrixHelpModal';
import './SystemControlsPage.css';

function toBool(v, fallback = false) {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') return v.trim().toLowerCase() === 'true';
  if (typeof v === 'number') return v !== 0;
  return fallback;
}

const DEFAULTS = {
  email_service_enabled: true,
  whatsapp_service_enabled: true,
};

function displayEffectiveValue(value) {
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  const text = String(value ?? '').trim();
  return text ? text : '(empty)';
}

function SubsectionCollapseToggle({ title, description, open, onToggle, controlsId }) {
  return (
    <button
      type="button"
      className="system-controls-subsection-toggle"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={controlsId}
    >
      <span className="system-controls-subsection-toggle-text">
        <span className="system-controls-subsection-title">{title}</span>
        {description ? <span className="system-controls-subsection-desc">{description}</span> : null}
      </span>
      <span className={`system-controls-chevron ${open ? 'is-open' : ''}`} aria-hidden="true">
        ▾
      </span>
    </button>
  );
}

function EffectiveRuntimeLegend() {
  return (
    <div className="effective-settings-legend" aria-label="Effective runtime legend">
      <span className="effective-settings-legend-item">
        <OverrideBadge kind="override" />
        <span className="effective-settings-legend-text">
          Override — saved value differs from default (left)
        </span>
      </span>
    </div>
  );
}

function EffectiveSettingRow({ item }) {
  const isOverride = item.source === 'db_override';
  const defaultValue = displayEffectiveValue(item.env_default);
  const effectiveValue = displayEffectiveValue(item.effective);
  const notes = [];
  if (item.note) {
    notes.push(item.note);
    if (item.db_override !== null && item.db_override !== undefined) {
      notes.push(`Stored override: ${displayEffectiveValue(item.db_override)}`);
    }
  }

  return (
    <div className={`effective-settings-row ${isOverride ? 'effective-settings-row-override' : ''}`}>
      <div className="effective-settings-row-main">
        <div className="effective-settings-name">{item.label}</div>
        {notes.length > 0 ? (
          <div className="effective-settings-detail-list">
            {notes.map((detail) => (
              <span key={detail} className="effective-settings-detail-chip">
                {detail}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div className="effective-settings-baseline">
        <span className="effective-settings-value effective-settings-value-baseline">{defaultValue}</span>
      </div>

      <div className="effective-settings-separator" aria-hidden="true">
        →
      </div>

      <div className="effective-settings-runtime">
        {isOverride ? (
          <>
            <span className="effective-settings-value effective-settings-value-override">{effectiveValue}</span>
            <OverrideBadge kind="override" />
          </>
        ) : (
          <span className="effective-settings-runtime-same">using default</span>
        )}
      </div>
    </div>
  );
}

function EffectiveSettingsGroup({ title, items }) {
  const entries = Object.entries(items || {});
  if (entries.length === 0) return null;

  return (
    <div className="effective-settings-group">
      <div className="effective-settings-group-title">{title}</div>
      <div className="effective-settings-list">
        {entries.map(([key, item]) => (
          <EffectiveSettingRow key={key} item={item} />
        ))}
      </div>
    </div>
  );
}

function NotificationMatrixSection({
  token,
  headers,
  onRuntimeSettingsUpdated,
  refreshTrigger = 0,
  emailEnabled = true,
  whatsappEnabled = true,
}) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [error, setError] = useState('');
  const [events, setEvents] = useState([]);
  const [dirty, setDirty] = useState({});

  const fetchPrefs = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.SYSTEM_CONTROLS.NOTIFICATION_PREFERENCES, { headers });
      const json = await res.json();
      if (!res.ok) {
        setError(json?.error || 'Failed to load notification preferences');
        setLoading(false);
        return;
      }
      setEvents(Array.isArray(json?.events) ? json.events : []);
      setDirty({});
      setLoaded(true);
      setLoading(false);
    } catch (e) {
      setError(String(e));
      setLoading(false);
    }
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded && !loading) {
      fetchPrefs();
    }
  };

  // Reset when auth token changes so the next open fetches fresh prefs
  useEffect(() => {
    setLoaded(false);
    setEvents([]);
    setDirty({});
    setError('');
    if (open) {
      fetchPrefs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!refreshTrigger) return;
    setLoaded(false);
    setDirty({});
    if (open) {
      fetchPrefs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const rows = useMemo(() => {
    return events.map((ev) => {
      const patch = dirty[ev.event_key];
      if (!patch) return ev;
      return {
        ...ev,
        enabled: patch.enabled ?? ev.enabled,
        channels: { ...ev.channels, ...(patch.channels || {}) },
      };
    });
  }, [events, dirty]);

  const hasOverrides = useMemo(() => events.some((ev) => ev.is_override), [events]);

  const setEnabled = (eventKey, enabled) => {
    setDirty((prev) => {
      const base = events.find((e) => e.event_key === eventKey) || {};
      const cur = prev[eventKey] || {
        enabled: base.enabled,
        channels: { ...(base.channels || {}) },
      };
      return {
        ...prev,
        [eventKey]: { ...cur, enabled },
      };
    });
  };

  const setChannel = (eventKey, channel, value) => {
    setDirty((prev) => {
      const base = events.find((e) => e.event_key === eventKey) || {};
      const cur = prev[eventKey] || {
        enabled: base.enabled,
        channels: { ...(base.channels || {}) },
      };
      return {
        ...prev,
        [eventKey]: {
          ...cur,
          channels: { ...cur.channels, [channel]: value },
        },
      };
    });
  };

  const save = async () => {
    const preferences = Object.entries(dirty).map(([event_key, patch]) => ({
      event_key,
      enabled: patch.enabled,
      channels: patch.channels,
    }));
    if (preferences.length === 0) {
      toast.info('No notification preference changes to save.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.SYSTEM_CONTROLS.NOTIFICATION_PREFERENCES, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ preferences }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.error || `Failed to save preferences (HTTP ${res.status})`;
        setError(msg);
        toast.error(msg);
        setSaving(false);
        return;
      }
      setEvents(Array.isArray(json?.events) ? json.events : []);
      setDirty({});
      toast.success('Notification preferences saved.');
      await onRuntimeSettingsUpdated?.();
    } catch (e) {
      const msg = String(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    setResetting(true);
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.SYSTEM_CONTROLS.NOTIFICATION_PREFERENCES_RESET, {
        method: 'POST',
        headers,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.error || `Failed to reset preferences (HTTP ${res.status})`;
        setError(msg);
        toast.error(msg);
        setResetting(false);
        return;
      }
      setEvents(Array.isArray(json?.events) ? json.events : []);
      setDirty({});
      toast.success('Notification preferences reset to matrix defaults.');
      await onRuntimeSettingsUpdated?.();
    } catch (e) {
      const msg = String(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="system-controls-subsection">
      <SubsectionCollapseToggle
        title="Notification matrix controls"
        description="Turns channels open/closed per event for this org. Who receives each channel is fixed by persona mapping (see Help) — not editable here."
        open={open}
        onToggle={toggleOpen}
        controlsId="notification-matrix-panel"
      />

      {open ? (
        <div id="notification-matrix-panel" className="system-controls-subsection-panel">
          <div className="system-controls-subsection-toolbar">
            <OverrideBadgeLegend text="Saved value differs from build matrix default" />
            <div className="system-controls-subsection-toolbar-actions action-palette">
              <button
                type="button"
                className="btn-refresh"
                onClick={() => setHelpOpen(true)}
                title="Persona mapping and how these toggles work"
                aria-label="Open notification matrix help"
              >
                ℹ️
              </button>
              <button
                type="button"
                className="btn-refresh"
                onClick={fetchPrefs}
                disabled={saving || resetting || loading}
                title="Refresh notification matrix"
                aria-label="Refresh notification matrix"
              >
                🔄
              </button>
              <button
                type="button"
                className="action-btn action-secondary"
                onClick={() => setResetModalOpen(true)}
                disabled={saving || resetting || loading || !hasOverrides}
              >
                <span className="action-text">{resetting ? 'Resetting…' : 'Reset'}</span>
              </button>
              <button
                type="button"
                className="action-btn action-primary"
                onClick={save}
                disabled={saving || resetting || Object.keys(dirty).length === 0}
              >
                <span className="action-text">{saving ? 'Saving…' : 'Save'}</span>
              </button>
            </div>
          </div>
          <hr className="system-controls-subsection-divider" />

          <NotificationMatrixHelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />

          <ConfirmationModal
            isOpen={resetModalOpen}
            entityName="Notification matrix preferences"
            entityType="settings"
            action="reset"
            actionVerb="reset"
            customMessage="This will delete all saved org overrides and revert every event to the build matrix defaults."
            isLoading={resetting}
            onCancel={() => setResetModalOpen(false)}
            onConfirm={async () => {
              setResetModalOpen(false);
              await resetToDefaults();
            }}
          />

          {error ? <div className="system-controls-error">{error}</div> : null}

          {loading ? (
            <div className="system-controls-state">Loading notification preferences…</div>
          ) : (
            <>
              {!emailEnabled || !whatsappEnabled ? (
                <div className="system-controls-inline-hint" role="status">
                  {!emailEnabled && !whatsappEnabled
                    ? 'Email and WhatsApp channels are off — matrix Email/WhatsApp toggles will not send until channels are enabled above.'
                    : !emailEnabled
                      ? 'Email channel is off — matrix Email toggles will not send until Email is enabled above.'
                      : 'WhatsApp channel is off — matrix WhatsApp toggles will not send until WhatsApp is enabled above.'}
                </div>
              ) : null}
              <div className="notif-matrix-table-wrap">
                <table className="notif-matrix-table">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Category</th>
                      <th>Enabled</th>
                      <th>In-app</th>
                      <th>Email</th>
                      <th>WhatsApp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const allowed = row.allowed_channels || {};
                      const channels = row.channels || {};
                      const overridden = row.overridden_fields || {};
                      return (
                        <tr
                          key={row.event_key}
                          className={row.is_override ? 'notif-matrix-override' : ''}
                        >
                          <td>
                            <div className="notif-matrix-event-key">{row.event_key}</div>
                            <div className="notif-matrix-event-desc">{row.description}</div>
                          </td>
                          <td>{row.category}</td>
                          <td>
                            <span className="notif-matrix-cell-control">
                              <input
                                type="checkbox"
                                checked={!!row.enabled}
                                onChange={(e) => setEnabled(row.event_key, e.target.checked)}
                              />
                              {overridden.enabled ? <OverrideBadge /> : <span className="notif-matrix-cell-dot-slot" aria-hidden="true" />}
                            </span>
                          </td>
                          {['in_app', 'email', 'whatsapp'].map((ch) => (
                            <td key={ch}>
                              {!allowed[ch] ? (
                                <span className="notif-matrix-na" title="N/A in matrix">
                                  —
                                </span>
                              ) : (
                                <span className="notif-matrix-cell-control">
                                  <input
                                    type="checkbox"
                                    checked={!!channels[ch]}
                                    disabled={!row.enabled}
                                    onChange={(e) => setChannel(row.event_key, ch, e.target.checked)}
                                  />
                                  {overridden[ch] ? (
                                    <OverrideBadge />
                                  ) : (
                                    <span className="notif-matrix-cell-dot-slot" aria-hidden="true" />
                                  )}
                                </span>
                              )}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function SystemControlsPage({ token, developerSettingsAvailable }) {
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [controls, setControls] = useState(DEFAULTS);
  const [envDefaults, setEnvDefaults] = useState(DEFAULTS);
  const [overrideKeys, setOverrideKeys] = useState([]);
  const [devOpen, setDevOpen] = useState(false);
  const [effectiveOpen, setEffectiveOpen] = useState(false);
  const [effectiveSettings, setEffectiveSettings] = useState(null);
  const [effectiveError, setEffectiveError] = useState('');
  const [pageRefreshNonce, setPageRefreshNonce] = useState(0);

  const headers = useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const fetchEffectiveSettings = async () => {
    setEffectiveError('');
    try {
      const res = await fetch(API_ENDPOINTS.SYSTEM_CONTROLS.EFFECTIVE, { headers });
      const json = await res.json();
      if (!res.ok) {
        setEffectiveError(json?.error || 'Failed to load effective settings');
        return;
      }
      setEffectiveSettings(json);
    } catch (e) {
      setEffectiveError(String(e));
    }
  };

  const fetchControls = async ({ soft = false } = {}) => {
    if (!soft) setLoading(true);
    setError('');
    setForbidden(false);
    try {
      const res = await fetch(API_ENDPOINTS.SYSTEM_CONTROLS.GET, { headers });
      if (res.status === 403) {
        setForbidden(true);
        setLoading(false);
        return;
      }
      const json = await res.json();
      if (!res.ok) {
        setError(json?.error || 'Failed to load system controls');
        setLoading(false);
        return;
      }

      setEnvDefaults({
        ...DEFAULTS,
        ...(json?.env_defaults || {}),
      });
      setOverrideKeys(Array.isArray(json?.override_keys) ? json.override_keys : []);

      const c = json?.controls || {};
      setControls({
        email_service_enabled: toBool(c.email_service_enabled, true),
        whatsapp_service_enabled: toBool(c.whatsapp_service_enabled, true),
      });

      await fetchEffectiveSettings();

      setLoading(false);
    } catch (e) {
      setError(String(e));
      setLoading(false);
    }
  };

  const refreshPage = async () => {
    await fetchControls({ soft: true });
    setPageRefreshNonce((n) => n + 1);
  };

  useEffect(() => {
    fetchControls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.SYSTEM_CONTROLS.UPDATE, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ controls }),
      });
      if (res.status === 403) {
        setForbidden(true);
        setError('System controls are disabled in this environment (403).');
        setSaving(false);
        return;
      }

      let json = null;
      try {
        json = await res.json();
      } catch (e) {
        json = null;
      }
      if (!res.ok) {
        const msg = json?.error || `Failed to save notification channels (HTTP ${res.status})`;
        setError(msg);
        toast.error(msg);
        setSaving(false);
        return;
      }
      await fetchControls({ soft: true });
      toast.success('Notification channels saved.');
    } catch (e) {
      const msg = String(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="system-controls-page">
        <div className="system-controls-state">Loading System Controls…</div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="system-controls-page">
        <div className="system-controls-state">System controls are disabled in this environment.</div>
      </div>
    );
  }

  const hasEmailOverride = overrideKeys.includes('email_service_enabled');
  const hasWhatsappOverride = overrideKeys.includes('whatsapp_service_enabled');

  return (
    <div className="system-controls-page">
      <div className="system-controls-header">
        <div className="system-controls-header-left">
          <div>
            <div className="system-controls-title-row">
              <h2 className="system-controls-title">🎛️ System Controls</h2>
              <button
                type="button"
                className="btn-refresh"
                onClick={refreshPage}
                disabled={saving}
                title="Refresh all settings"
                aria-label="Refresh all settings"
              >
                🔄
              </button>
            </div>
            <p className="system-controls-subtitle">Operational switches (superadmin only).</p>
          </div>
        </div>
      </div>

      {error && <div className="system-controls-error">{error}</div>}

      <div className="system-controls-subsection">
        <div className="system-controls-subsection-header">
          <div>
            <div className="system-controls-subsection-title">Notification channels</div>
            <div className="system-controls-subsection-desc">
              Master on/off for Email and WhatsApp. When a channel is off, sending for that channel is a no-op
              regardless of matrix toggles below.
            </div>
          </div>
        </div>

        <div className="system-controls-subsection-panel system-controls-channels-panel">
          <div className="system-controls-subsection-toolbar">
            <OverrideBadgeLegend text="Saved value differs from default" />
            <div className="system-controls-subsection-toolbar-actions action-palette">
              <button
                type="button"
                className="btn-refresh"
                onClick={() => fetchControls({ soft: true })}
                disabled={saving}
                title="Refresh notification channels"
                aria-label="Refresh notification channels"
              >
                🔄
              </button>
              <button type="button" className="action-btn action-primary" onClick={save} disabled={saving}>
                <span className="action-text">{saving ? 'Saving…' : 'Save'}</span>
              </button>
            </div>
          </div>
          <hr className="system-controls-subsection-divider" />

          <label className="system-controls-row">
            <input
              type="checkbox"
              checked={controls.email_service_enabled}
              onChange={(e) => setControls((p) => ({ ...p, email_service_enabled: e.target.checked }))}
            />
            <span>
              <span className="system-controls-label">Email enabled</span>{' '}
              <span className="system-controls-default">
                (default: {envDefaults.email_service_enabled ? 'enabled' : 'disabled'})
                {hasEmailOverride ? (
                  <>
                    {' '}
                    <OverrideBadge title="Saved value differs from default" />
                  </>
                ) : null}
              </span>
            </span>
          </label>

          <label className="system-controls-row">
            <input
              type="checkbox"
              checked={controls.whatsapp_service_enabled}
              onChange={(e) => setControls((p) => ({ ...p, whatsapp_service_enabled: e.target.checked }))}
            />
            <span>
              <span className="system-controls-label">WhatsApp enabled</span>{' '}
              <span className="system-controls-default">
                (default: {envDefaults.whatsapp_service_enabled ? 'enabled' : 'disabled'})
                {hasWhatsappOverride ? (
                  <>
                    {' '}
                    <OverrideBadge title="Saved value differs from default" />
                  </>
                ) : null}
              </span>
            </span>
          </label>
        </div>
      </div>

      <hr className="system-controls-divider" />

      <NotificationMatrixSection
        token={token}
        headers={headers}
        onRuntimeSettingsUpdated={fetchEffectiveSettings}
        refreshTrigger={pageRefreshNonce}
        emailEnabled={controls.email_service_enabled}
        whatsappEnabled={controls.whatsapp_service_enabled}
      />

      <hr className="system-controls-divider" />

      {developerSettingsAvailable ? (
        <>
          <div className="system-controls-subsection">
            <SubsectionCollapseToggle
              title="Developer Settings"
              description="Demo/test overrides (separate from System Controls)."
              open={devOpen}
              onToggle={() => setDevOpen((v) => !v)}
              controlsId="developer-settings-panel"
            />

            {devOpen ? (
              <div id="developer-settings-panel" className="system-controls-subsection-body">
                <DeveloperSettingsCard
                  token={token}
                  embedded
                  refreshTrigger={pageRefreshNonce}
                  onRuntimeSettingsUpdated={fetchEffectiveSettings}
                />
              </div>
            ) : null}
          </div>
        </>
      ) : null}

      <hr className="system-controls-divider" />

      <div className="system-controls-subsection">
        <SubsectionCollapseToggle
          title="Effective Runtime Settings"
          description="Read-only view of values currently applied by the running backend."
          open={effectiveOpen}
          onToggle={() => {
            setEffectiveOpen((v) => !v);
            if (!effectiveOpen) fetchEffectiveSettings();
          }}
          controlsId="effective-settings-panel"
        />

        {effectiveOpen ? (
          <div id="effective-settings-panel" className="effective-settings-panel">
            {effectiveError ? <div className="system-controls-error">{effectiveError}</div> : null}
            {effectiveSettings ? (
              <>
                <EffectiveRuntimeLegend />
                <EffectiveSettingsGroup title="Notification Channels" items={effectiveSettings.system_controls} />
                {(() => {
                  const matrixOverrides = effectiveSettings.notification_matrix_overrides || {};
                  const overrideCount = Object.keys(matrixOverrides).length;
                  if (overrideCount === 0) {
                    return (
                      <div className="effective-settings-group">
                        <div className="effective-settings-group-title">Notification matrix overrides</div>
                        <div className="effective-settings-empty">
                          No notification preference overrides — runtime uses build matrix defaults.
                        </div>
                      </div>
                    );
                  }
                  return (
                    <EffectiveSettingsGroup
                      title={`Notification matrix overrides (${overrideCount})`}
                      items={matrixOverrides}
                    />
                  );
                })()}
                {effectiveSettings.developer_settings?.available ? (
                  <EffectiveSettingsGroup
                    title={`Developer Settings ${
                      effectiveSettings.developer_settings.overrides_enabled
                        ? '(overrides enabled)'
                        : '(overrides disabled)'
                    }`}
                    items={effectiveSettings.developer_settings.settings}
                  />
                ) : (
                  <div className="effective-settings-empty">Developer Settings are disabled in this environment.</div>
                )}
              </>
            ) : (
              <div className="effective-settings-empty">No effective settings loaded yet.</div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
